import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import NoResults from '../../../pages/shared/NoResults'
import styles from './index.module.scss'
export default function TicketDetailsTable ({ headers, data }) {
  const [expandedYears, setExpandedYears] = useState({})
  const [expandedMonths, setExpandedMonths] = useState({})

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }))
  }

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`
    setExpandedMonths(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const getTableData = useCallback(() => {
    const yearWiseData = {}
    data?.forEach((item) => {
      const { year, month, day } = item
      yearWiseData[year] = yearWiseData[year] || {}
      yearWiseData[year][month] = yearWiseData[year][month] || {}
      yearWiseData[year][month][day] = yearWiseData[year][month][day] || []
      yearWiseData[year][month][day].push(item)
    })
    return yearWiseData
  }, [data])
  const tableData = useMemo(getTableData, [getTableData])
  useEffect(() => {
    const allEntries = Object.keys(tableData)
    if (allEntries.length > 0) {
      const expandedState = allEntries.reduce((acc, year) => {
        acc[year] = true
        return acc
      }, {})
      setExpandedYears(expandedState)
    }
  }, [tableData])

  return (
    <div className={styles.tableContainer}>
      <div className='row'>
        <div className='col-xs-12 table-responsive'>
          <table className='table'>
            <thead>
              <tr>
                <th width='1%' className={styles.td}>+</th>
                <th width='3%' className={styles.td}>
                  <div className='cursor-pointer'>
                    Year
                  </div>
                </th>
                {headers.map((header, i) => (
                  <th key={i} width='10%' className={styles.tdCenter}>
                    <div className='cursor-pointer'>
                      {header}
                    </div>
                  </th>
                ))}
                <th width='4%' className={styles.tdCenter}>
                  <div className='cursor-pointer'>
                    Total
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              { headers.length === 0 ? (<tr><td colSpan={12}><NoResults /></td></tr>) : null }
              {Object.entries(tableData)?.sort(([year1], [year2]) => year2 - year1).map(([year, yearData], index) => {
                const isYearOpen = expandedYears[year]

                return (
                  <React.Fragment key={index}>
                    <tr className={styles.yearRow}>
                      <td className={styles.td}>
                        <button onClick={() => toggleYear(year)}>
                          {isYearOpen ? '-' : '+'}
                        </button>
                      </td>
                      <td className={styles.td}><b>{year}</b></td>
                      {headers.map((header, headerIndex) => {
                        let sum = 0
                        Object.values(yearData).forEach(monthData => {
                          Object.values(monthData).forEach(tickets => {
                            sum += tickets
                              .filter(t => t.ticketType === header)
                              .reduce((a, b) => a + b.tickets, 0)
                          })
                        })
                        return <td key={headerIndex} className={styles.tdCenter}>{sum}</td>
                      })}
                      <td className={styles.tdCenter}>
                        {
                          Object.values(yearData).flatMap(monthData =>
                            Object.values(monthData).flatMap(dayData =>
                              dayData.map(t => t.tickets)
                            )
                          ).reduce((a, b) => a + b, 0)
                        }
                      </td>
                    </tr>

                    {isYearOpen && Object.entries(yearData).map(([month, monthData], monthIndex) => {
                      const isMonthOpen = expandedMonths[`${year}-${month}`]

                      return (
                        <React.Fragment key={`${index}-${monthIndex}`}>
                          <tr className={styles.monthRow}>
                            <td className={styles.td}>
                              <button onClick={() => toggleMonth(year, month)}>
                                {isMonthOpen ? '-' : '+'}
                              </button>
                            </td>
                            <td className={styles.td}>{month}</td>
                            {headers.map((header, headerIndex) => {
                              let sum = 0
                              Object.values(monthData).forEach(tickets => {
                                sum += tickets
                                  .filter(t => t.ticketType === header)
                                  .reduce((a, b) => a + b.tickets, 0)
                              })
                              return <td key={headerIndex} className={styles.tdCenter}>{sum}</td>
                            })}
                            <td className={styles.tdCenter}>
                              {
                                Object.values(monthData).flatMap(tickets =>
                                  tickets.map(t => t.tickets)
                                ).reduce((a, b) => a + b, 0)
                              }
                            </td>
                          </tr>

                          {isMonthOpen && Object.entries(monthData).map(([day, dayTickets], dayIndex) => (
                            <tr key={`${index}-${monthIndex}-${dayIndex}`}>
                              <td className={styles.td}></td>
                              <td className={styles.td}>{day}</td>
                              {headers.map((header, headerIndex) => {
                                const sum = dayTickets
                                  .filter(t => t.ticketType === header)
                                  .reduce((a, b) => a + b.tickets, 0)
                                return <td key={headerIndex} className={styles.tdCenter}>{sum}</td>
                              })}
                              <td className={styles.tdCenter}>
                                {
                                  dayTickets.reduce((a, b) => a + b.tickets, 0)
                                }
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      )
                    })}
                  </React.Fragment>
                )
              })}

              <tr className={styles.yearRow}>
                <td className={styles.td}>-</td>
                <td className={styles.td}><b>Total</b></td>
                {headers.map((header, i) => {
                  let total = 0
                  Object.values(tableData).forEach(yearData =>
                    Object.values(yearData).forEach(monthData =>
                      Object.values(monthData).forEach(tickets => {
                        total += tickets
                          .filter(t => t.ticketType === header)
                          .reduce((a, b) => a + b.tickets, 0)
                      }
                      )
                    )
                  )
                  return <td key={i} className={styles.tdCenter}>{total}</td>
                })}
                <td className={styles.tdCenter}>
                  {
                    Object.values(tableData).flatMap(yearData =>
                      Object.values(yearData).flatMap(monthData =>
                        Object.values(monthData).flatMap(tickets =>
                          tickets.map(t => t.tickets)
                        )
                      )
                    ).reduce((a, b) => a + b, 0)
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

TicketDetailsTable.propTypes = {
  headers: PropTypes.array,
  data: PropTypes.array
}
